import { apiClient } from './api.client';

export interface TrialRegistrationData {
  manufacturerName: string;
  contactEmail: string;
  contactPhone?: string;
  password: string;
  businessType?: 'PROPRIETORSHIP' | 'PARTNERSHIP' | 'LLP' | 'PRIVATE_LIMITED' | 'PUBLIC_LIMITED' | 'OTHER';
  expectedVolume?: number;
}

export interface TrialRegistrationResponse {
  success: boolean;
  message: string;
  data?: {
    trialAccountId: string;
    status: string;
    manufacturerName: string;
    contactEmail: string;
  };
  errors?: Array<{
    field: string;
    message: string;
  }>;
  code?: string;
}

export interface EmailCheckResponse {
  success: boolean;
  available: boolean;
  message: string;
}

export interface TrialStatusResponse {
  success: boolean;
  data?: {
    id: string;
    status: string;
    createdAt: string;
    startDate?: string;
    endDate?: string;
    budgetAllocated: number;
    budgetUsed: number;
  };
  message?: string;
}

class TrialRegistrationService {
  /**
   * Submit trial registration
   */
  async registerForTrial(data: TrialRegistrationData): Promise<TrialRegistrationResponse> {
    try {
      const response = await apiClient.post('/trial-registration', data);
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        return error.response.data;
      }
      throw new Error('Failed to submit trial registration');
    }
  }

  /**
   * Check if email is available for registration
   */
  async checkEmailAvailability(email: string): Promise<EmailCheckResponse> {
    try {
      const response = await apiClient.get(`/trial-registration/check-email/${encodeURIComponent(email)}`);
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        return error.response.data;
      }
      throw new Error('Failed to check email availability');
    }
  }

  /**
   * Get trial registration status
   */
  async getTrialStatus(trialAccountId: string): Promise<TrialStatusResponse> {
    try {
      const response = await apiClient.get(`/trial-registration/status/${trialAccountId}`);
      return response.data;
    } catch (error: any) {
      if (error.response?.data) {
        return error.response.data;
      }
      throw new Error('Failed to get trial status');
    }
  }

  /**
   * Validate email format
   */
  validateEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  /**
   * Validate phone number format
   */
  validatePhone(phone: string): boolean {
    // Basic international phone number validation
    const phoneRegex = /^[\+]?[1-9][\d]{0,15}$/;
    return phoneRegex.test(phone.replace(/[\s\-\(\)]/g, ''));
  }

  /**
   * Validate manufacturer name
   */
  validateManufacturerName(name: string): boolean {
    return name.trim().length >= 2 && name.trim().length <= 100;
  }

  /**
   * Validate password
   */
  validatePassword(password: string): boolean {
    return password.length >= 8;
  }

  /**
   * Get business type options
   */
  getBusinessTypeOptions() {
    return [
      { value: 'PROPRIETORSHIP', label: 'Proprietorship' },
      { value: 'PARTNERSHIP', label: 'Partnership' },
      { value: 'LLP', label: 'Limited Liability Partnership (LLP)' },
      { value: 'PRIVATE_LIMITED', label: 'Private Limited Company' },
      { value: 'PUBLIC_LIMITED', label: 'Public Limited Company' },
      { value: 'OTHER', label: 'Other' }
    ];
  }

  /**
   * Format trial status for display
   */
  formatTrialStatus(status: string): string {
    switch (status) {
      case 'PENDING_TRIAL_APPROVAL':
        return 'Pending Approval';
      case 'TRIAL_ACTIVE':
        return 'Active Trial';
      case 'TRIAL_EXPIRED':
        return 'Trial Expired';
      case 'FULL_REGISTRATION_PENDING_APPROVAL':
        return 'Full Registration Pending';
      case 'ACTIVE':
        return 'Active Account';
      case 'BLOCKED':
        return 'Blocked';
      default:
        return status;
    }
  }

  /**
   * Get status color for UI
   */
  getStatusColor(status: string): string {
    switch (status) {
      case 'PENDING_TRIAL_APPROVAL':
        return 'orange';
      case 'TRIAL_ACTIVE':
        return 'green';
      case 'TRIAL_EXPIRED':
        return 'red';
      case 'FULL_REGISTRATION_PENDING_APPROVAL':
        return 'blue';
      case 'ACTIVE':
        return 'green';
      case 'BLOCKED':
        return 'red';
      default:
        return 'gray';
    }
  }
}

export const trialRegistrationService = new TrialRegistrationService();